import { Form } from "@unform/web";
import { Building, DollarSign, Pencil, TrendingDown, TrendingUp, X } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { Zoom, toast } from "react-toastify";
import { handleUpdatedFields } from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../../../";
import Textarea from "~/components/RegisterForm/Textarea";
import AccountsMultiSelect from "./AccountsMultiSelect";
import api from "~/services/api";
import FindGeneric from "~/components/Finds/FindGeneric";

export const InputContext = createContext({});

const dateTypeOptions = [
  { value: "accrual_date", label: "Accrual Date" },
  { value: "settlement_date", label: "Settlement Date" },
  { value: "due_date", label: "Due Date" },
];

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const auth = useSelector((state) => state.auth);
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    loaded: false,
    dre_name: "",
    dre_code: "",
    period: "",
    status: "",
    date_type: "due_date",
    gross_revenues_accounts: [],
    deductions_accounts: [],
    expenses_accounts: [],
    filial_id: null,
  });

  const [selectedDateType, setSelectedDateType] = useState("due_date");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedFilial, setSelectedFilial] = useState(null);
  
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [loading, setLoading] = useState(false);

  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleGeneralFormSubmit(data) {
    data.date_type = selectedDateType;
    data.status = selectedStatus;

    console.log("DADOS RECEBIDOS DO FORMULÁRIO:", data);

    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }

    if (id === "new") {
      setLoading(true);

      const payload = {
        dre_name: data.dre_name,
        dre_code: data.dre_code,
        status: data.status,
        date_type: data.date_type,
        description: data.description || "",
        gross_revenues_accounts: data.gross_revenues_accounts || [],
        deductions_accounts: data.deductions_accounts || [],
        expenses_accounts: data.expenses_accounts || [],
      };

    
      if (auth?.filial?.id === 1) {
        const chosenFilialId = selectedFilial?.id || data?.filial?.id;
        if (!chosenFilialId) {
          setLoading(false);
          toast("Selecione a Filial para esta configuração", {
            autoClose: 2000,
            type: "error",
            transition: Zoom,
          });
          return;
        }
        payload.filial_id = Number(chosenFilialId);
      }

      api
        .post("/dreconfigs/create-config", payload)
        .then(({ data: response }) => {
          setPageData({ ...pageData, ...response });
          setSuccessfullyUpdated(true);
          toast("DRE Configuration created successfully!", {
            autoClose: 2000,
            type: "success",
            transition: Zoom,
          });
          setLoading(false);
          handleOpened(null);
        })
        .catch((err) => {
          console.error("Error creating DRE config:", err);
          toast(err?.response?.data?.error || "Error creating DRE configuration!", {
            autoClose: 3000,
            type: "error",
            transition: Zoom,
          });
          setLoading(false);
        });
    } else if (id !== "new") {
      const updated = handleUpdatedFields(data, pageData);
      const filialChanged = auth?.filial?.id === 1 && String(pageData.filial_id || "") !== String(selectedFilialId || "");

      if (updated.length > 0 || filialChanged) {
        const objUpdated = Object.fromEntries(updated);

        setLoading(true);

        const payload = {
          dre_name: objUpdated.dre_name,
          dre_code: objUpdated.dre_code,
          status: objUpdated.status,
          date_type: objUpdated.date_type,
          description: objUpdated.description,
          gross_revenues_accounts: objUpdated.gross_revenues_accounts,
          deductions_accounts: objUpdated.deductions_accounts,
          expenses_accounts: objUpdated.expenses_accounts,
        };

      

        api
          .put(`/dreconfigs/update-config/${id}`, payload)
          .then(({ data: response }) => {
            setPageData({ ...pageData, ...response });
            setSuccessfullyUpdated(true);
            toast("DRE Configuration updated successfully!", {
              autoClose: 2000,
              type: "success",
              transition: Zoom,
            });
            setLoading(false);
            handleOpened(null);
          })
          .catch((err) => {
            console.error("Error updating DRE config:", err);
            toast(err?.response?.data?.error || "Error updating DRE configuration!", {
              autoClose: 3000,
              type: "error",
              transition: Zoom,
            });
            setLoading(false);
          });
      } else {
        toast("No changes detected!", {
          autoClose: 1500,
          type: "info",
          transition: Zoom,
        });
      }
    }
  }

  async function loadDREConfig() {
    if (id !== "new") {
      setLoading(true);
      try {
        const { data } = await api.get(`/dreconfig/${id}`);
        console.log("DRE Config loaded from API:", data);

        const loadedData = {
          loaded: true,
          dre_name: data.dre_name || "",
          dre_code: data.dre_code || "",
          status: data.status || "",
          date_type: data.date_type || "due_date",
          description: data.description || "",
          gross_revenues_accounts: data.gross_revenues_accounts || [],
          deductions_accounts: data.deductions_accounts || [],
          expenses_accounts: data.expenses_accounts || [],
          filial_id: data.filial_id ?? null,
        };

        console.log("Setting pageData:", loadedData);
        setPageData(loadedData);
        setSelectedDateType(loadedData.date_type);
        setSelectedStatus(loadedData.status);
       

        setTimeout(() => {
          if (generalForm.current) {
            console.log("Setting form data:", loadedData);
            generalForm.current.setData(loadedData);
          }
        }, 100);

      } catch (err) {
        console.error("❌ Error loading DRE config:", err);
        toast(err?.response?.data?.error || "Error loading DRE configuration!", {
          autoClose: 3000,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    } else {
      setPageData((prev) => ({ ...prev, loaded: true }));
    }
  }

  useEffect(() => {
    if (id === "new") {
      setFormType("full");
    }
    loadDREConfig();
  }, [id]);

  

  useEffect(() => {
    if (pageData.loaded && generalForm.current && id !== "new") {
      console.log("pageData changed, updating form with:", pageData);
      generalForm.current.setData({
        dre_name: pageData.dre_name,
        dre_code: pageData.dre_code,
        status: pageData.status,
        date_type: pageData.date_type,
        description: pageData.description,
        gross_revenues_accounts: pageData.gross_revenues_accounts,
        deductions_accounts: pageData.deductions_accounts,
        expenses_accounts: pageData.expenses_accounts,
      });
    }
  }, [pageData.loaded]);

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.dre_name}</h2>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
              >
                <Building size={16} /> General
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="revenues"
              >
                <DollarSign size={16} /> Revenues
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="expenses"
              >
                <TrendingDown size={16} /> Expenses
              </RegisterFormMenu>
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <InputContext.Provider
                    value={{
                      id,
                      generalForm,
                      setSuccessfullyUpdated,
                      fullscreen,
                      setFullscreen,
                      successfullyUpdated,
                      handleCloseForm,
                    }}
                  >
                    {id === "new" || pageData.loaded ? (
                      <>
                        <FormHeader
                          access={access}
                          title={pageData.dre_name}
                          registry={registry}
                          InputContext={InputContext}
                        />

                        <InputLineGroup title="GENERAL" activeMenu={activeMenu === "general"}>
                          {/* Filial selector row (first), like Student Preview */}
                          {id === "new" && (
                            <FindGeneric
                              route="filials"
                              title="Filial"
                              scope="filial"
                              required
                              InputContext={InputContext}
                              defaultValue={
                                selectedFilial
                                  ? selectedFilial
                                  : (id === "new" && auth.filial.id !== 1
                                    ? { id: auth.filial.id, name: auth.filial.name }
                                    : { id: pageData.filial?.id, name: pageData.filial?.name })
                              }
                              setReturnFindGeneric={(row) => setSelectedFilial(row)}
                              fields={[{ title: "Name", name: "name" }]}
                            />
                          )}

                          <InputLine title="Basic Information">
                            <Input type="text" name="dre_name" required title="DRE Name" grow defaultValue={pageData.dre_name} InputContext={InputContext} />
                            <Input type="text" name="dre_code" required title="DRE Code" grow defaultValue={pageData.dre_code} InputContext={InputContext} />
                          </InputLine>
                          <InputLine title="Details">
                            <div className="w-full">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status <span className="text-red-500">*</span>
                              </label>
                              <select
                                name="status"
                                required
                                value={selectedStatus}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                                onChange={(e) => {
                                  setSelectedStatus(e.target.value);
                                  setSuccessfullyUpdated(false);
                                }}
                              >
                                <option value="">Select...</option>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                                <option value="Draft">Draft</option>
                              </select>
                            </div>
                            <div className="w-full">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date Type <span className="text-red-500">*</span>
                              </label>
                              <select
                                name="date_type"
                                required
                                value={selectedDateType}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                                onChange={(e) => {
                                  setSelectedDateType(e.target.value);
                                  setSuccessfullyUpdated(false);
                                }}
                              >
                                {dateTypeOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                            {null}
                          </InputLine>
                          <InputLine title="Description">
                            <Textarea name="description" title="Description" rows={4} grow InputContext={InputContext} placeholder="Optional notes..." />
                          </InputLine>
                        </InputLineGroup>

                        <InputLineGroup title="REVENUES" activeMenu={activeMenu === "revenues"}>
                          <AccountsMultiSelect
                            key={`gross-${pageData.loaded}-${pageData.gross_revenues_accounts?.length}`}
                            title="Gross Revenues"
                            name="gross_revenues_accounts"
                            options={[]}
                            placeholder="Search accounts (e.g., Revenue)"
                            InputContext={InputContext}
                            useApi={true}
                            filterPrefix="01"
                            defaultValue={pageData.gross_revenues_accounts}
                          />
                          <AccountsMultiSelect
                            key={`deductions-${pageData.loaded}-${pageData.deductions_accounts?.length}`}
                            title="Deductions from Gross Revenue"
                            name="deductions_accounts"
                            options={[]}
                            placeholder="Search accounts (e.g., Deduction)"
                            InputContext={InputContext}
                            useApi={true}
                            filterPrefix="02"
                            defaultValue={pageData.deductions_accounts}
                          />
                        </InputLineGroup>

                        <InputLineGroup title="EXPENSES" activeMenu={activeMenu === "expenses"}>
                          <AccountsMultiSelect
                            key={`expenses-${pageData.loaded}-${pageData.expenses_accounts?.length}`}
                            title="Expenses"
                            name="expenses_accounts"
                            options={[]}
                            placeholder="Search accounts (e.g., Payroll)"
                            InputContext={InputContext}
                            useApi={true}
                            filterPrefix="02"
                            defaultValue={pageData.expenses_accounts}
                          />
                        </InputLineGroup>

                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </InputContext.Provider>
                </Form>
              </div>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
